import {
  Conversation,
  ChatMessage,
  SendMessageData,
  ChatResponse,
  PaginatedConversationsResponse,
} from '../schemas/chat';
import { AIModel } from '../types/models';
import { modelService } from './modelService';
import { researchAPI } from './api/researchApi';
import { fileAPI } from './api/fileApi';
import http from '../lib/api';
import { tokenManager } from '../lib/auth';

class ChatService {
  private token: string | null = null;

  constructor() {
    // Use tokenManager for consistent token management
    this.token = tokenManager.getAccessToken();
  }

  // Conversation Management - Updated to match backend endpoints
  async getConversations(
    page: number = 1,
    pageSize: number = 20,
    options?: {
      include_archived?: boolean;
      include_pinned?: boolean;
      folder_id?: string;
      search?: string;
    }
  ): Promise<PaginatedConversationsResponse | Conversation[]> {
    const params = {
      page,
      page_size: pageSize,
      ...options,
    };

    const response = await researchAPI.getResearchChats(params);
    const data = response.data as any;

    // Transform API response to match expected type
    if (data && typeof data === 'object' && 'items' in data) {
      return {
        items: data.items,
        total: data.total || 0,
        page: data.page || page,
        page_size: data.page_size || pageSize,
        totalPages: data.total_pages || Math.ceil((data.total || 0) / pageSize),
      } as PaginatedConversationsResponse;
    }

    return data as Conversation[];
  }

  async getConversationById(id: string): Promise<Conversation> {
    const response = await researchAPI.getResearchChat(id);
    return response.data as unknown as Conversation;
  }

  async createConversation(data: {
    title?: string;
    model_id?: string;
    description?: string;
    knowledge_base_ids?: string[];
    folder_id?: string;
    literature_sources?: string[];
    literature_options?: any;
  }): Promise<Conversation> {
    const response = await researchAPI.createResearchChat({
      ...data,
      literature_sources: data.literature_sources || [],
    });
    return response.data as unknown as Conversation;
  }

  async updateConversation(id: string, data: Partial<Conversation>): Promise<Conversation> {
    // Filter out null values from description before sending to API
    const updateData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== null) {
        updateData[key] = value;
      }
    }
    const response = await researchAPI.updateResearchChat(id, updateData as any);
    return response.data as unknown as Conversation;
  }

  async deleteConversation(id: string): Promise<void> {
    await researchAPI.deleteResearchChat(id);
  }

  async archiveConversation(id: string): Promise<Conversation> {
    const response = await researchAPI.updateResearchChat(id, {
      is_archived: true,
    } as any);
    return response.data as unknown as Conversation;
  }

  async pinConversation(id: string, pinned: boolean = true): Promise<Conversation> {
    const response = await researchAPI.updateResearchChat(id, {
      is_pinned: pinned,
    } as any);
    return response.data as unknown as Conversation;
  }

  // Message Management - Updated to match backend endpoints
  async sendMessage(_conversationId: string, _data: SendMessageData): Promise<ChatResponse> {
    // Note: Research API doesn't have non-streaming sendMessage, so we'll use the stream and wait for completion
    // For now, return a mock response since we're moving to streaming-only
    throw new Error(
      'Non-streaming sendMessage not supported by research API. Use sendMessageStream instead.'
    );
  }

  async updateMessage(
    _conversationId: string,
    _messageId: string,
    _content: string
  ): Promise<ChatMessage> {
    // API doesn't support update directly, this would need to be implemented
    // For now, throw an error to indicate this operation is not supported
    throw new Error('Message update is not supported by the current API');
  }

  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      const response = await researchAPI.getResearchMessages(conversationId);

      // Response format: { messages: [...], has_more: bool }
      const messages = (response.data as any)?.messages || response.data || [];

      if (!Array.isArray(messages)) {
        console.warn('getMessages: Expected array, got:', typeof messages);
        return [];
      }

      // Convert API MessageResponse format to ChatMessage format
      return messages.map(
        (msg: {
          id: string;
          chat_id?: string;
          role: string;
          content: string;
          created_at: string;
          model_id?: string | null;
          model_name?: string | null;
          is_done?: boolean;
          prompt_tokens?: number;
          completion_tokens?: number;
          total_tokens?: number;
          files?: any[];
          sources?: any[];
          thinking_process?: { steps: number; content: string } | null;
          error_data?: any | null;
          parent_id?: string | null;
          children_ids?: string[];
        }) => ({
          id: msg.id,
          chat_id: msg.chat_id,
          role: msg.role as 'user' | 'assistant' | 'system',
          content: msg.content,
          created_at: msg.created_at,
          model_id: msg.model_id,
          model_name: msg.model_name,
          is_done: msg.is_done ?? true,
          thinking_process: msg.thinking_process,
          files: msg.files || [],
          sources: msg.sources || [],
          error_data: msg.error_data,
          parent_id: msg.parent_id,
          children_ids: msg.children_ids,
          usage:
            msg.prompt_tokens || msg.completion_tokens || msg.total_tokens
              ? {
                  promptTokens: msg.prompt_tokens || 0,
                  completionTokens: msg.completion_tokens || 0,
                  totalTokens: msg.total_tokens || 0,
                }
              : undefined,
          prompt_tokens: msg.prompt_tokens,
          completion_tokens: msg.completion_tokens,
          total_tokens: msg.total_tokens,
        })
      );
    } catch (error) {
      console.error('Error getting messages:', error);
      return [];
    }
  }

  async deleteMessage(_conversationId: string, _messageId: string): Promise<void> {
    // Research API doesn't have deleteMessage endpoint
    throw new Error('Message deletion is not supported by research API.');
  }

  async regenerateMessage(_conversationId: string, _messageId: string): Promise<ChatResponse> {
    // Research API doesn't have regenerateMessage endpoint
    throw new Error('Message regeneration is not supported by research API.');
  }

  // Streaming Chat (for real-time responses) - Updated to match backend endpoints
  async sendMessageStream(
    conversationId: string,
    data: SendMessageData,
    onChunk?: (chunk: string) => void,
    onThinkingChunk?: (chunk: string) => void,
    onComplete?: (message: ChatMessage) => void,
    onError?: (error: Error) => void,
    onTitleChunk?: (title: string) => void
  ): Promise<ChatMessage> {
    try {
      // Transform files array for backend API
      // Backend expects file IDs as strings, but we might have FileMetadata objects
      const filesForApi = (data.files || [])
        .map(f => {
          if (typeof f === 'string') return f;
          if (typeof f === 'object' && f !== null) {
            return (f as any).file_id || (f as any).id || '';
          }
          return String(f);
        })
        .filter(id => id); // Remove empty strings

      const response = await researchAPI.streamResearchMessage(conversationId, {
        ...data,
        files: filesForApi,
        literature_sources: (data as any).literature_sources,
        literature_options: (data as any).literature_options,
      } as any);

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Use the stream processing utility from stream.ts
      const { processStream } = await import('../lib/stream');
      const reader = response.body.getReader();

      const result = await processStream(
        reader,
        onChunk,
        onThinkingChunk,
        onComplete
          ? (message: {
              id: string;
              role: 'assistant';
              content: string;
              thinking_process?: { steps: number; content: string };
              created_at: string;
              model_name?: string;
              is_done: boolean;
            }) => {
              onComplete(message as ChatMessage);
            }
          : undefined,
        onTitleChunk
      );
      return result as ChatMessage;
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
      throw error;
    }
  }

  /**
   * Resume streaming a message that was interrupted
   * @param conversationId The ID of the conversation
   * @param messageId The ID of the message to resume
   * @param onChunk Callback for each content chunk
   * @param onThinkingChunk Callback for thinking chunks
   * @param onComplete Callback when stream completes
   * @param onError Callback for errors
   * @returns Promise with the complete message
   */
  async resumeMessageStream(
    conversationId: string,
    messageId: string,
    onChunk?: (chunk: string) => void,
    onThinkingChunk?: (chunk: string) => void,
    onComplete?: (message: ChatMessage) => void,
    onError?: (error: Error) => void
  ): Promise<ChatMessage> {
    try {
      console.log(
        `[chatService] Resuming stream for message ${messageId} in conversation ${conversationId}`
      );
      const response = await researchAPI.resumeMessageStream(conversationId, messageId);

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Use the stream processing utility from stream.ts
      const { processStream } = await import('../lib/stream');
      const reader = response.body.getReader();

      const result = await processStream(
        reader,
        onChunk,
        onThinkingChunk,
        onComplete
          ? (message: {
              id: string;
              role: 'assistant';
              content: string;
              thinking_process?: { steps: number; content: string };
              created_at: string;
              model_name?: string;
              is_done: boolean;
            }) => {
              onComplete(message as ChatMessage);
            }
          : undefined
      );
      return result as ChatMessage;
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
      throw error;
    }
  }

  async getFileUploadStatus(
    fileId: string
  ): Promise<{ status: string; progress?: number; error?: string }> {
    const response = await fileAPI.getFileInfo(fileId);
    return {
      status: response.data.status,
      error: response.data.error_message || undefined,
    };
  }

  async deleteFile(fileId: string): Promise<void> {
    await fileAPI.deleteFile(fileId);
  }

  // Conversation Sharing - Updated to match backend endpoints
  async shareConversation(
    _id: string,
    _isPublic: boolean = true
  ): Promise<{ shareUrl: string; shareId: string }> {
    // Research API doesn't have sharing endpoints
    throw new Error('Conversation sharing is not supported by research API.');
  }

  async getSharedConversation(_shareId: string): Promise<Conversation> {
    // Research API doesn't have sharing endpoints
    throw new Error('Shared conversations are not supported by research API.');
  }

  // Tags and Folders - Updated to match backend endpoints
  async addTagToConversation(_id: string, _tag: string): Promise<Conversation> {
    // Research API doesn't have tag endpoints
    throw new Error('Tags are not supported by research API.');
  }

  async removeTagFromConversation(_id: string, _tag: string): Promise<Conversation> {
    // Research API doesn't have tag endpoints
    throw new Error('Tags are not supported by research API.');
  }

  async moveConversationToFolder(id: string, folderId?: string): Promise<Conversation> {
    // Use updateResearchChat for folder updates
    const response = await researchAPI.updateResearchChat(id, { folder_id: folderId });
    return response.data as unknown as Conversation;
  }

  // Authentication
  setToken(token: string): void {
    this.token = token;
    // Use consistent token key that matches tokenManager
    localStorage.setItem('access_token', token);
  }

  clearToken(): void {
    this.token = null;
    // Use consistent token key that matches tokenManager
    localStorage.removeItem('access_token');
  }

  // Ollama-specific methods
  async sendMessageWithModel(
    conversationId: string,
    data: SendMessageData,
    modelId?: string
  ): Promise<ChatResponse> {
    // If a model is specified, use the model service for chat completion
    if (modelId) {
      return this.sendMessageViaModel(conversationId, data, modelId);
    }

    // Otherwise, use the standard chat endpoint
    return this.sendMessage(conversationId, data);
  }

  private async sendMessageViaModel(
    conversationId: string,
    data: SendMessageData,
    modelId: string
  ): Promise<ChatResponse> {
    try {
      // Get conversation history for context
      const conversation = await this.getConversationById(conversationId);

      // Build message history for the model
      const messages: any[] = [
        ...(conversation.messages || []).map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        {
          role: 'user',
          content: data.content,
        },
      ];

      // Use model service for chat completion
      const response = await modelService.createChatCompletion({
        model: modelId,
        messages,
        temperature: data.temperature || 0.7,
        max_tokens: data.max_tokens || 2048,
        params: {},
      });

      // Create chat message object from response
      const assistantMessage: ChatMessage = {
        id: response.id,
        role: 'assistant',
        content: response.choices[0]?.message?.content || '',
        created_at: new Date().toISOString(),
        model_name: response.model || undefined,
        is_done: true,
        usage: response.usage
          ? {
              promptTokens: response.usage.prompt_tokens,
              completionTokens: response.usage.completion_tokens,
              totalTokens: response.usage.total_tokens,
            }
          : undefined,
      };

      // Save the message to the conversation
      const savedMessage = await this.saveMessage(conversationId, assistantMessage);

      return {
        id: response.id,
        message: savedMessage,
        usage: response.usage
          ? {
              promptTokens: response.usage.prompt_tokens,
              completionTokens: response.usage.completion_tokens,
              totalTokens: response.usage.total_tokens,
            }
          : undefined,
        finishReason: response.choices[0]?.finish_reason || undefined,
      };
    } catch (error) {
      // Model service fallback: try regular endpoint
      return this.sendMessage(conversationId, { ...data, model_id: modelId });
    }
  }

  async streamMessageWithModel(
    conversationId: string,
    data: SendMessageData,
    modelId: string,
    onChunk?: (chunk: string) => void,
    onComplete?: (message: ChatMessage) => void,
    onError?: (error: Error) => void
  ): Promise<ChatMessage> {
    try {
      // Get conversation history for context
      const conversation = await this.getConversationById(conversationId);

      // Build message history for the model
      const messages: any[] = [
        ...(conversation.messages || []).map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        {
          role: 'user',
          content: data.content,
        },
      ];

      let accumulatedContent = '';
      const messageId = '';

      // Use model service for streaming chat completion
      await modelService.streamChatCompletion(
        {
          model: modelId,
          messages,
          temperature: data.temperature || 0.7,
          max_tokens: data.max_tokens || 2048,
          params: {},
        },
        (chunk: string) => {
          accumulatedContent += chunk;
          if (onChunk) {
            onChunk(chunk);
          }
        }
      );

      // Create final message
      const assistantMessage: ChatMessage = {
        id: messageId || `msg-${Date.now()}`,
        role: 'assistant',
        content: accumulatedContent,
        created_at: new Date().toISOString(),
        model_name: modelId,
        is_done: true,
      };

      // Save the message to the conversation
      const savedMessage = await this.saveMessage(conversationId, assistantMessage);

      if (onComplete) {
        onComplete(savedMessage);
      }

      return savedMessage;
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
      throw error;
    }
  }

  private async saveMessage(conversationId: string, message: ChatMessage): Promise<ChatMessage> {
    // This would be implemented based on your backend API for saving messages
    // For now, return the message as-is
    return message;
  }

  async getAvailableModels(): Promise<AIModel[]> {
    try {
      return await modelService.getModels({ is_active: true });
    } catch (error) {
      // Fallback: return empty array
      return [];
    }
  }

  async discoverOllamaModels(baseUrl?: string): Promise<AIModel[]> {
    try {
      return await modelService.discoverOllamaModels(baseUrl);
    } catch (error) {
      throw new Error(
        `Failed to discover Ollama models: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async testModel(
    modelId: string,
    testMessage: string = 'Hello, how are you?'
  ): Promise<{ response: string; latency: number }> {
    try {
      return await modelService.testModel(modelId, testMessage);
    } catch (error) {
      throw new Error(
        `Failed to test model ${modelId}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  // Utility methods
  isAuthed(): boolean {
    return !!this.token;
  }

  async testConnection(): Promise<{ status: string; authenticated: boolean }> {
    try {
      const response = await http.get('/health');
      const data = response.data;
      return {
        status: 'connected',
        authenticated: this.isAuthed(),
        ...(data || {}),
      };
    } catch (error) {
      return {
        status: 'disconnected',
        authenticated: false,
      };
    }
  }
}

export const chatService = new ChatService();
export default chatService;
